import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomImage, Text, warningDialog, confirmDialog } from '@src/components';

import OrderSummaryInformation from './OrderSummaryInformation';
import CardDetailInformation from './CardDetailInformation';

import {
  instructionAdd,
  nextStep,
  setActiveStep,
  setResetDetailList,
  getInstructionsPageList,
  setTotalAutoLoading,
  setResetInstructionsTempList,
  setDetailList,
  setAutoLoadingId,
  setIntegrationId,
  updateAutoLoadingCommand,
  instructionDeleteTemp,
  setKeepIntegrationId,
} from '@src/store/slice/autoUploadInstructionsSlice';

import { isCreditCardUser } from '@src/utils/utils';

import { Button } from 'antd';
import iconBack from '@src/assets/icons/icon-back.svg';
import '@src/styles/autoUploadInstruction/orderSummary.scss';

const OrderSummary = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isExit, setIsExit] = useState(false);
  const { customerVou } = useSelector((state) => state?.customer);
  const { totalAutoLoading, integrationId, autoLoadingId, activeStep } = useSelector(
    (state) => state?.autoUploadInstructions,
  );

  useEffect(() => {
    const unblock = history.block((location) => {
      if (isExit) {
        return true;
      }

      if (
        activeStep === 1 &&
        location.pathname !== history.location.pathname &&
        totalAutoLoading?.id > 0
      ) {
        setIsExit(true);
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: <Text t="autoUploadBeforeUnloadMessage" />,
          okText: <Text t="yes" />,
          cancelText: <Text t="no" />,
          onOk: async (e) => {
            if (e?.target?.className?.split(' ')?.includes('close-image')) return;
            if (totalAutoLoading?.id > 0) {
              const body = { id: totalAutoLoading?.id };
              await dispatch(instructionDeleteTemp(body));
            }
            setIsExit(true);
            history.push(location.pathname);
          },
          onCancel: () => {
            setIsExit(false);
            return false;
          },
        });

        unblock();
      } else {
        return true;
      }

      return false;
    });

    return unblock;
  }, [activeStep, history, isExit, dispatch, totalAutoLoading]);

  const handleOrderApproval = useCallback(async () => {
    if (!isCreditCardUser(customerVou)) {
      const body = {
        autoLoadingCreateRequestDto: {
          periodType: totalAutoLoading?.periodType,
          confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
          startDay: totalAutoLoading?.startDay,
          orderProcessType: totalAutoLoading?.orderProcessType,
          monthChoice: totalAutoLoading?.chooseMonth,
        },
      };

      if (totalAutoLoading?.totalPrice === 0) {
        warningDialog({
          title: <Text t="balanceApproval" />,
          message: 'Siparişinizi onaylamak için lütfen en az bir karta tutar giriniz',
        });
        return;
      }

      if (integrationId && integrationId > 0) {
        body.integrationId = integrationId;
      }

      if (autoLoadingId && autoLoadingId > 0) {
        body.autoLoadingId = totalAutoLoading?.id;
      }

      const action = await dispatch(instructionAdd(body));

      if (instructionAdd.fulfilled.match(action)) {
        await dispatch(setActiveStep(3));
        await dispatch(
          setTotalAutoLoading({
            orderProcessType: 2,
            periodType: 1,
            startDay: 1,
            confirmEveryMonth: totalAutoLoading?.confirmEveryMonth,
            totalCount: 0,
            totalPrice: 0,
            totalPaymentPrice: 0,
            vouId: 1,
            segId: 1000,
            monthChoice: false,
            monthlyPrice: 0.0,
          }),
        );
      }
    } else {
      dispatch(nextStep());
    }
  }, [dispatch, totalAutoLoading?.totalPrice, customerVou]);

  const handleCancelOrder = useCallback(async () => {
    await dispatch(setKeepIntegrationId(false));
    if (autoLoadingId && autoLoadingId !== 0) {
      const res = await dispatch(
        updateAutoLoadingCommand({
          id: autoLoadingId,
          confirmEveryMonth: 0,
          startDay: 0,
          isActive: false,
          monthChoice: false,
        }),
      );

      if (updateAutoLoadingCommand.fulfilled.match(res)) {
        await dispatch(setAutoLoadingId(0));
        await dispatch(setResetDetailList([]));
        await dispatch(setDetailList([]));
        await dispatch(setResetInstructionsTempList([]));
        await dispatch(setActiveStep(0));
        await dispatch(
          setTotalAutoLoading({
            orderProcessType: 2,
            periodType: 1,
            startDay: 1,
            confirmEveryMonth: 0,
            totalCount: 0,
            totalPrice: 0,
            totalPaymentPrice: 0,
            vouId: 1,
            segId: 1000,
            monthChoice: false,
            monthlyPrice: 0.0,
          }),
        );
        await dispatch(getInstructionsPageList());
      }
    }
    if (integrationId && integrationId !== 0) {
      await dispatch(
        updateAutoLoadingCommand({
          integrationId: integrationId,
          confirmEveryMonth: 0,
          startDay: 0,
          isActive: false,
          monthChoice: false,
        }),
      );

      if (updateAutoLoadingCommand.fulfilled.match(res)) {
        await dispatch(setIntegrationId(0));
        await dispatch(setResetDetailList([]));
        await dispatch(setDetailList([]));
        await dispatch(setResetInstructionsTempList([]));
        await dispatch(setActiveStep(0));
        await dispatch(
          setTotalAutoLoading({
            orderProcessType: 2,
            periodType: 1,
            startDay: 1,
            confirmEveryMonth: 0,
            totalCount: 0,
            totalPrice: 0,
            totalPaymentPrice: 0,
            vouId: 1,
            segId: 1000,
            monthChoice: false,
            monthlyPrice: 0.0,
          }),
        );
        await dispatch(getInstructionsPageList());
      }
    }
  }, [dispatch]);

  const turnBack = useCallback(async () => {
    await dispatch(setKeepIntegrationId(true));
    await dispatch(setActiveStep(0));
  }, [dispatch, history]);

  return (
    <div className="autoUploadInstruction-instruction-summary-content">
      <OrderSummaryInformation />

      <div className="step-btn-container-summary-balance">
        <Button
          id="autoUploadInstructions-orderSummary-main-back-btn"
          className="back-btn"
          onClick={turnBack}
        >
          <CustomImage src={iconBack} />
          <Text t="turnBack" />
        </Button>

        <div className={'right-btn-container'}>
          <Button
            id="autoUploadInstructions-orderSummary-main-cancel-btn"
            className="cancel-btn"
            onClick={handleCancelOrder}
          >
            <Text t="cancelInstruction" />
          </Button>

          <Button
            id="autoUploadInstructions-orderSummary-main-next-btn"
            className="next-btn"
            onClick={handleOrderApproval}
          >
            <Text t="approveInstruction" />
          </Button>
        </div>
      </div>

      <CardDetailInformation />
    </div>
  );
};

export default OrderSummary;
